/**
 * optionBuilder.ts
 * -------------------------------------------------------
 * 职责：将 MapViewContext + ScatterDatum[] → 完整 EChartsOption。
 *       包含 geo3D 配置、tooltip、series 组装。
 * -------------------------------------------------------
 */

import type { ECharts, EChartsOption } from 'echarts';
import type { TooltipComponentFormatterCallbackParams } from 'echarts';
import {
    GEO_REGION_HEIGHT,
    GEO3D_EMPHASIS_HOVER,
} from '../mapConstants';
import {
    CHINA_NORMAL_LABEL,
    CHINA_EMPHASIS_LABEL,
    CHINA_HIGHLIGHT_LABEL,
    PROVINCE_NORMAL_LABEL,
    PROVINCE_EMPHASIS_LABEL,
} from './labelStyles';
import { buildScatterSeries } from './scatterBuilder';
import { buildPolicyScatterSeries } from './policyScatterBuilder';
import { getGeo3DCoordinateSystem } from './geoPickHelper';
import type { MapViewContext, PolicyScatterDatum, ScatterDatum } from '../types';

/* ================================================================
 *  geo3D 区域标签 formatter
 * ================================================================ */

export function geo3DRegionLabelFormatter(params: { name?: string }): string {
    return typeof params?.name === 'string' ? params.name : '';
}

/* ================================================================
 *  tooltip formatter
 * ================================================================ */

function scatterTooltipLevelLabel(d: ScatterDatum): string {
    if (d.parkLevel === 'national') return '国家级';
    if (d.parkLevel === 'provincial') return '省级';
    if (d.parkLevel === 'pending') {
        return '待开发';
    }
    return '';
}

function mapTooltipFormatter(
    params: TooltipComponentFormatterCallbackParams,
): string {
    const item = Array.isArray(params) ? params[0] : params;
    const fallbackName =
        typeof item?.name === 'string' ? item.name : '';
    const raw = item?.data as ScatterDatum | PolicyScatterDatum | undefined;
    if (!raw?.name) {
        if (fallbackName && /条/.test(fallbackName)) {
            return `${fallbackName}<br/><span style="opacity:.85">政策聚合</span>`;
        }
        return '';
    }
    if ('policyCount' in raw && typeof raw.policyCount === 'number') {
        const pd = raw as PolicyScatterDatum;
        return `${pd.name}<br/><span style="opacity:.85">${pd.levelTag}政策聚合</span>`;
    }
    const d = raw as ScatterDatum;
    const level = scatterTooltipLevelLabel(d);
    return `${d.name}<br/><span style="opacity:.85">${level}</span>`;
}

/* ================================================================
 *  省份宽高比 → boxWidth & aspectScale 自适应
 * ================================================================ */

interface BoxLayout {
    boxWidth: number;
    aspectScale: number;
}

/**
 * 根据省份经纬度宽高比，计算合适的 boxWidth 和 aspectScale。
 *
 * geoAspectRatio = lngSpan / latSpan
 *   > 1 → 横向省份（如内蒙古），boxWidth 大
 *   < 1 → 竖向省份（如宁夏），boxWidth 小、aspectScale 调高
 */
function resolveProvinceBoxLayout(ratio: number | null): BoxLayout {
    if (ratio == null) return { boxWidth: 75, aspectScale: 0.84 };

    // 用查找表代替 if-else 链，更易维护
    const thresholds: Array<{ max: number; boxWidth: number; aspectScale: number }> = [
        { max: 0.6, boxWidth: 30, aspectScale: 1.1 },  // 极窄竖条（宁夏、台湾）
        { max: 0.85, boxWidth: 45, aspectScale: 1.0 },  // 偏竖（甘肃、山西）
        { max: 1.15, boxWidth: 60, aspectScale: 0.9 },  // 近方形
        { max: 1.6, boxWidth: 70, aspectScale: 0.84 },  // 偏横
        { max: Infinity, boxWidth: 85, aspectScale: 0.78 }, // 极横（内蒙古）
    ];

    const match = thresholds.find((t) => ratio < t.max)!;
    return { boxWidth: match.boxWidth, aspectScale: match.aspectScale };
}

/* ================================================================
 *  高亮区域（全国地图高亮某省）
 * ================================================================ */

function buildGeo3DHighlightRegions(
    mapName: string,
    highlightProvinceName: string | null,
    highlightCityName: string | null,
) {
    if (mapName !== 'china' && highlightCityName) {
        return [
            {
                name: highlightCityName,
                itemStyle: {
                    color: '#1e5688',
                    opacity: 1,
                    borderWidth: 1.15,
                    borderColor: '#5df0ff',
                },
                label: {
                    show: true,
                    formatter: geo3DRegionLabelFormatter,
                    distance: 4,
                    ...CHINA_HIGHLIGHT_LABEL,
                },
            },
        ];
    }

    // 仅全国地图且有指定省名时才生效
    if (mapName !== 'china' || !highlightProvinceName) return undefined;

    return [
        {
            name: highlightProvinceName,
            itemStyle: {
                color: '#1e5688',
                opacity: 1,
                borderWidth: 1.15,
                borderColor: '#5df0ff',
            },
            label: {
                show: true,
                formatter: geo3DRegionLabelFormatter,
                distance: 4,
                ...CHINA_HIGHLIGHT_LABEL,
            },
        },
    ];
}

/* ================================================================
 *  geo3D 主体配置
 * ================================================================ */

function buildGeo3DOption(ctx: MapViewContext) {
    const isChina = ctx.mapName === 'china';

    // 根据全国/省份选择标签样式
    const normalLabel = isChina ? CHINA_NORMAL_LABEL : PROVINCE_NORMAL_LABEL;
    const emphasisLabel = isChina ? CHINA_EMPHASIS_LABEL : PROVINCE_EMPHASIS_LABEL;

    // 省份自适应布局
    const { boxWidth, aspectScale } = isChina
        ? { boxWidth: 100, aspectScale: 0.9 }
        : resolveProvinceBoxLayout(ctx.geoAspectRatio);

    return {
        map: ctx.mapName,
        roam: 'move',
        selectedMode: false,
        tooltip: { show: false },
        aspectScale,
        shading: 'lambert',
        environment: '#020818',
        boxWidth,
        regionHeight: GEO_REGION_HEIGHT,

        // 常态标签
        label: {
            show: true,
            formatter: geo3DRegionLabelFormatter,
            distance: isChina ? 1 : 1,
            ...normalLabel,
        },

        itemStyle: {
            color: '#15365c',
            opacity: 1,
            borderWidth: 0.85,
            borderColor: '#1ad1e8',
        },

        regions: buildGeo3DHighlightRegions(
            ctx.mapName,
            ctx.highlightProvinceGeoName,
            ctx.highlightCityGeoName,
        ),

        // hover 高亮
        emphasis: {
            ...GEO3D_EMPHASIS_HOVER,
            label: {
                show: true,
                formatter: geo3DRegionLabelFormatter,
                distance: isChina ? 1 : 1,
                ...emphasisLabel,
            },
        },

        // 灯光 & 后处理
        light: {
            main: { intensity: 1.25, shadow: true },
            ambient: { intensity: 0.42 },
        },
        postEffect: {
            enable: false,
            bloom: { enable: true, intensity: 0.06 },
            FXAA: { enable: true },
            colorCorrection: {
                enable: true,
                exposure: 0.02,
                brightness: 0.02,
                saturation: 1.08,
                contrast: 1.02,
            },
        },
        temporalSuperSampling: { enable: true },

        // 视角控制
        viewControl: {
            projection: 'orthographic',
            distance: ctx.viewDistance,
            alpha: ctx.viewAlpha,
            beta: ctx.viewBeta,
            minAlpha: 89,
            maxAlpha: 89,
            minBeta: 0,
            maxBeta: 0,
            minDistance: 18,
            maxDistance: Math.max(ctx.viewDistance * 1.5, 420),
            rotateSensitivity: 0,
            panSensitivity: 1,
            zoomSensitivity: 1,
            rotateMouseButton: 'right',
            panMouseButton: 'left',
            animation: false,
            animationDurationUpdate: 0,
            animationEasingUpdate: 'linear',
        },
    };
}

/* ================================================================
 *  最终输出 option
 * ================================================================ */

export function buildOption(
    data: ScatterDatum[],
    selectedParkId: string | null,
    ctx: MapViewContext,
    policyScatter: PolicyScatterDatum[] = [],
): EChartsOption {
    const series = [buildScatterSeries(data)];
    if (policyScatter.length) {
        series.push(buildPolicyScatterSeries(policyScatter));
    }
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'item',
            show: true,
            triggerOn: 'mousemove',
            appendToBody: true,
            confine: false,
            backgroundColor: 'rgba(8, 28, 58, 0.92)',
            borderColor: '#00e5e5',
            textStyle: { color: '#e6f7ff' },
            formatter: mapTooltipFormatter,
        },
        geo3D: buildGeo3DOption(ctx),
        series,
    } as EChartsOption;
}

/* ================================================================
 *  视图同步 & 安全刷新
 * ================================================================ */

/** 将 geo3D 相机中心对准指定经纬度 */
export function syncGeo3DViewCenter(
    chart: ECharts,
    targetLngLat: [number, number, number],
): void {
    const cs = getGeo3DCoordinateSystem(chart);
    if (!cs) return;
    const world = cs.dataToPoint(targetLngLat);
    chart.setOption({ geo3D: { viewControl: { center: world } } });
}

/**
 * 先隐藏 tooltip（避免残留指针状态导致的 echarts 内部报错），
 * 再在下一帧执行 option 更新。
 */
export function safeFlushPointerThenApply(
    chart: ECharts,
    apply: () => void,
): void {
    requestAnimationFrame(() => {
        try {
            chart.dispatchAction({ type: 'hideTip' });
        } catch { /* ignore */ }
        apply();
    });
}
